import { useCallback, useEffect, useState } from 'react'
import { Kbd } from './glyphs'
import { addRule, api, failureText, type Rule } from './api'
import { store } from './store'
import { age, Avatar } from './visuals'

interface Card {
  id: string
  from: { name: string; email: string }
  subject: string
  preview: string
  receivedAt: string
  important: boolean
  label: string
  reason: string
  person: boolean
  why: string
}
interface Proposal { kind: Rule['kind']; pattern: string; action: Rule['action']; label: string; days?: number; explanation: string }

type Phase = 'loading' | 'swiping' | 'thinking' | 'review' | 'saving' | 'error'

/**
 * Ten emails, picked by an agent, one at a time: ← don't show me, → show me.
 * Then the agent proposes a few rules from the answers, and Enter adds them.
 */
export function Tune({ onClose, onDone }: { onClose: () => void; onDone: (text: string) => void }) {
  const [phase, setPhase] = useState<Phase>('loading')
  const [cards, setCards] = useState<Card[]>([])
  const [index, setIndex] = useState(0)
  const [answers, setAnswers] = useState<Array<{ id: string; want: boolean }>>([])
  const [fling, setFling] = useState<'left' | 'right' | null>(null)
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [accepted, setAccepted] = useState<Set<number>>(new Set())
  const [error, setError] = useState('')

  const start = useCallback(async () => {
    setPhase('loading'); setError(''); setIndex(0); setAnswers([])
    try {
      const result = await api<{ cards: Card[] }>('/api/tune/start', { method: 'POST', body: '{}' })
      if (!result.cards.length) { setError('There’s no mail to tune with yet. Try again once your inbox has loaded.'); setPhase('error'); return }
      setCards(result.cards); setPhase('swiping')
    } catch (failure) { setError(failureText(failure)); setPhase('error') }
  }, [])
  useEffect(() => { void start() }, [start])

  const finish = useCallback(async (all: Array<{ id: string; want: boolean }>) => {
    setPhase('thinking')
    try {
      const result = await api<{ rules: Proposal[] }>('/api/tune/finish', { method: 'POST', body: JSON.stringify({ answers: all }) })
      setProposals(result.rules); setAccepted(new Set(result.rules.map((_, i) => i))); setPhase('review')
    } catch (failure) { setError(failureText(failure)); setPhase('error') }
  }, [])

  const answer = useCallback((want: boolean) => {
    if (phase !== 'swiping' || fling) return
    const card = cards[index]
    if (!card) return
    setFling(want ? 'right' : 'left')
    const all = [...answers, { id: card.id, want }]
    setAnswers(all)
    setTimeout(() => {
      setFling(null)
      if (index + 1 >= cards.length) void finish(all)
      else setIndex(index + 1)
    }, 230)
  }, [phase, fling, cards, index, answers, finish])

  const back = useCallback(() => {
    if (phase !== 'swiping' || !index) return
    setAnswers(answers.slice(0, -1)); setIndex(index - 1)
  }, [phase, index, answers])

  const save = useCallback(async () => {
    const chosen = proposals.filter((_, i) => accepted.has(i))
    if (!chosen.length) { onClose(); return }
    setPhase('saving')
    try {
      for (const rule of chosen) await addRule({ kind: rule.kind, pattern: rule.pattern, action: rule.action, label: rule.label })
      store.soon(50)
      onDone(`Added ${chosen.length} rule${chosen.length === 1 ? '' : 's'}. Re-sorting your mail…`)
      onClose()
    } catch (failure) { setError(failureText(failure)); setPhase('review') }
  }, [proposals, accepted, onClose, onDone])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      event.stopPropagation()
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return }
      if (phase === 'swiping') {
        if (event.key === 'ArrowLeft' || event.key === 'h') { event.preventDefault(); answer(false) }
        if (event.key === 'ArrowRight' || event.key === 'l') { event.preventDefault(); answer(true) }
        if (event.key === 'ArrowUp' || event.key === 'Backspace') { event.preventDefault(); back() }
      } else if (phase === 'review') {
        if (event.key === 'Enter') { event.preventDefault(); void save() }
        const n = Number(event.key)
        if (n >= 1 && n <= proposals.length) {
          event.preventDefault()
          setAccepted(previous => { const next = new Set(previous); next.has(n - 1) ? next.delete(n - 1) : next.add(n - 1); return next })
        }
      } else if (phase === 'error' && event.key === 'Enter') { event.preventDefault(); void start() }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [phase, answer, back, save, proposals.length, onClose, start])

  const card = cards[index]
  const next = cards[index + 1]
  return (
    <div className="tune view-in">
      <div className="tune-col">
        <header className="tune-head">
          <div>
            <h1>Show me what matters</h1>
            <p className="muted">{phase === 'review' || phase === 'saving' ? 'Here’s what I learned. Keep what’s right.' : 'A few emails from your inbox. Tell me which ones you’d want to see.'}</p>
          </div>
          {phase === 'swiping' && <div className="tune-progress"><span style={{ width: `${(index / cards.length) * 100}%` }} /><em>{index + 1} of {cards.length}</em></div>}
        </header>

        {phase === 'loading' && <div className="tune-status"><span className="spinner" />Picking emails to ask you about…</div>}
        {phase === 'thinking' && <div className="tune-status"><span className="spinner" />Turning your answers into rules…</div>}
        {phase === 'error' && <div className="tune-status"><span className="warn">{error}</span><button className="link" onClick={() => void start()}>Try again ↵</button></div>}

        {phase === 'swiping' && card && (
          <div className="deck">
            {next && <div className="tune-card behind" aria-hidden="true" />}
            <article key={card.id} className={`tune-card${fling ? ` fling-${fling}` : ''}`}>
              <div className="tune-from">
                <Avatar name={card.from.name} email={card.from.email} person={card.person} size={32} />
                <div>
                  <strong>{card.from.name || card.from.email}</strong>
                  <span className="faint small">{card.from.email} · {age(card.receivedAt)} ago</span>
                </div>
              </div>
              <h2>{card.subject}</h2>
              <p className="tune-preview">{card.preview}</p>
              <div className="tune-meta">
                <span>Sorted now: {card.important ? 'Important' : `Other · ${card.label}`}{card.reason ? ` · ${card.reason}` : ''}</span>
                {card.why && <span className="faint">{card.why}</span>}
              </div>
              <div className={`stamp left${fling === 'left' ? ' on' : ''}`}>Skip</div>
              <div className={`stamp right${fling === 'right' ? ' on' : ''}`}>Show me</div>
            </article>
          </div>
        )}

        {phase === 'swiping' && (
          <div className="tune-keys">
            <button className="tune-key" onClick={() => answer(false)}><Kbd k="←" />Don’t need to see</button>
            {index > 0 && <button className="link small" onClick={back}><Kbd k="↑" />Back</button>}
            <button className="tune-key want" onClick={() => answer(true)}>Want to see<Kbd k="→" /></button>
          </div>
        )}

        {(phase === 'review' || phase === 'saving') && (
          <div className="proposals">
            {proposals.length === 0 && <p className="muted">Your mail is already sorted the way you’d want. Nothing to change.</p>}
            {proposals.map((rule, i) => (
              <button key={i} className={`proposal${accepted.has(i) ? ' on' : ''}`} style={{ animationDelay: `${i * 60}ms` }}
                onClick={() => setAccepted(previous => { const next = new Set(previous); next.has(i) ? next.delete(i) : next.add(i); return next })}>
                <span className="check">{accepted.has(i) ? '✓' : ''}</span>
                <span className="proposal-text">
                  <strong>{rule.kind === 'age' ? `Mail older than ${rule.days ?? rule.pattern} days goes to Other` : rule.kind === 'text' ? rule.pattern : rule.kind === 'sender' ? `Always ${rule.action === 'other' ? 'Other' : 'Important'}: ${rule.pattern}` : `Always ${rule.action === 'other' ? 'Other' : 'Important'}: anything from ${rule.pattern}`}</strong>
                  <span className="faint small">{rule.explanation}{rule.kind === 'text' ? ` · ${rule.action === 'other' ? `to Other as “${rule.label}”` : 'to Important'}` : ''}</span>
                </span>
                <Kbd k={i + 1} />
              </button>
            ))}
            {error && <p className="warn small">{error}</p>}
            <div className="tune-keys">
              <button className="link small" onClick={onClose}><Kbd k="esc" />Skip</button>
              <button className="primary" onClick={() => void save()} disabled={phase === 'saving'}>{phase === 'saving' ? 'Adding…' : proposals.length ? `Keep ${accepted.size} ↵` : 'Done ↵'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
